(() => {
  const storageKey = "evp.garage.v2";
  const previousStorageKey = "evp.garage.tags.v1";
  const selectionKey = "evp.garage.tags.selection.v1";
  // Local-only cap until vehicles are saved to customer accounts.
  const maxVehicles = 5;
  const gid = (id) => `gid://shopify/Metaobject/${id}`;
  const handle = (value) =>
    String(value)
      .toLowerCase()
      .replace(/['’]/g, "")
      .replace(/[^a-z0-9_]+/g, "-")
      .replace(/^-|-$/g, "");
  let catalogPromise;
  let garageIndex = 0;
  async function initialize(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = "true";
    const radioGroup = `vehicle-garage-${++garageIndex}`;
    const dialog = root.querySelector("dialog");
    const form = root.querySelector("[data-garage-form]");
    const [type, year, make, model] = ["type", "year", "make", "model"].map(
      (name) => form.querySelector(`[name="${name}"]`),
    );
    const submit = form.querySelector('[type="submit"]');
    const open = root.querySelector("[data-garage-open]");
    const filter = root.querySelector("[data-vehicle-filter]");
    const list = root.querySelector("[data-garage-list]");
    const add = root.querySelector("[data-garage-add]");
    const status = root.querySelector("[data-garage-status]");
    let models;
    try {
      catalogPromise ||= fetch(root.dataset.catalogUrl)
        .then((response) => {
          if (!response.ok) throw new Error("Vehicle catalogue unavailable");
          return response.json();
        })
        .catch((error) => {
          catalogPromise = null;
          throw error;
        });
      const catalog = await catalogPromise;
      if (!root.isConnected) return;
      models = Object.entries(catalog).flatMap(([type, makes]) =>
        Object.entries(makes).flatMap(([makeName, branch]) =>
          Object.entries(branch.models).map(([modelName, md]) => {
            const model = [type, makeName, modelName].map(handle).join("/");
            return {
              type,
              make: handle(makeName),
              makeName,
              model,
              modelName,
              years: Object.fromEntries(
                md.years.map((year) => [String(year), `${model}/${year}`]),
              ),
            };
          }),
        ),
      );
    } catch (_) {
      status.textContent =
        "Vehicle selection is unavailable. Please reload to try again.";
      open.disabled = true;
      return;
    }
    const resolve = (saved) => {
      if (!saved) return null;
      const md = models.find(
        (m) =>
          (m.model === saved.model && m.make === saved.make) ||
          (m.type === saved.type &&
            m.makeName === saved.makeName &&
            m.modelName === saved.modelName),
      );
      if (!md || (saved.year && !md.years[String(saved.year)])) return null;
      return {
        ...md,
        year: saved.year ? String(saved.year) : "",
        id: saved.year ? md.years[String(saved.year)] : md.model,
      };
    };
    const compact = (v) => ({
      key: v.id,
      source: "vehicle-data.json",
      makeName: v.makeName,
      modelName: v.modelName,
      type: v.type,
      make: v.make,
      model: v.model,
      year: v.year,
    });
    const label = (v) =>
      [v.year, v.makeName, v.modelName].filter(Boolean).join(" ");
    let garage = [],
      unresolved = [],
      unresolvedSelection = null,
      selected = null;
    try {
      const saved = JSON.parse(
        localStorage.getItem(storageKey) ||
          localStorage.getItem(previousStorageKey),
      );
      const unique = new Map();
      (Array.isArray(saved?.vehicles) ? saved.vehicles : []).forEach((v) => {
        const valid = resolve(v);
        if (valid) unique.set(valid.id, valid);
        else if (v && typeof v === "object") unresolved.push(v);
      });
      garage = [...unique.values()];
      selected =
        resolve(saved?.selection) ||
        garage.find((v) => v.id === saved?.active) ||
        null;
      if (!selected && saved?.selection) unresolvedSelection = saved.selection;
      if (!selected)
        selected = resolve(JSON.parse(localStorage.getItem(selectionKey)));
    } catch (_) {}
    const url = new URL(location.href);
    let applied = null;
    let clearFiltersOnClose = false;
    const tagPath = url.pathname.split("/collections/")[1]?.split("/")[1] || "";
    const activeTags = tagPath
      .split("+")
      .map((tag) => decodeURIComponent(tag).toLowerCase());
    const taggedModels = models.filter(
      (md) =>
        activeTags.includes("make_" + handle(md.makeName)) &&
        activeTags.includes("model_" + handle(md.modelName)),
    );
    if (taggedModels.length === 1) {
      const md = taggedModels[0];
      const year = Object.keys(md.years).find((y) =>
        activeTags.includes("year_" + y),
      );
      applied = resolve({ ...md, year: year || "" });
    }
    // Read the old IDs only to preserve prototype selections during migration.
    try {
      if (
        !localStorage.getItem(storageKey) &&
        !localStorage.getItem(previousStorageKey)
      ) {
        const legacy = JSON.parse(
          root.querySelector("[data-garage-catalog]").textContent,
        );
        const oldGarage = JSON.parse(localStorage.getItem("evp.garage.v1"));
        const migrate = (old) => {
          for (const [type, branch] of Object.entries(legacy?.types || {})) {
            for (const mk of branch.makes) {
              const md = mk.models.find(
                (m) => m.id === old?.model && mk.id === old?.make,
              );
              if (md)
                return resolve({
                  make: handle(mk.name),
                  model: [type, mk.name, md.name].map(handle).join("/"),
                  year: old.year,
                });
            }
          }
          return null;
        };
        garage = (oldGarage?.vehicles || []).map(migrate).filter(Boolean);
        const oldActive = (oldGarage?.vehicles || []).find((v) => {
          for (const branch of Object.values(legacy?.types || {})) {
            for (const mk of branch.makes) {
              const md = mk.models.find((m) => m.id === v.model);
              if (md && (md.years[v.year] || md.id) === oldGarage.active)
                return true;
            }
          }
          return false;
        });
        selected = migrate(oldActive) || selected;
        for (const branch of Object.values(legacy?.types || {})) {
          for (const mk of branch.makes)
            for (const md of mk.models) {
              for (const [year, id] of Object.entries(md.years)) {
                if (
                  url.searchParams.get("filter.p.m.fitment.vehicles") ===
                  gid(id)
                )
                  selected =
                    migrate({ make: mk.id, model: md.id, year }) || selected;
              }
            }
        }
        persist();
      }
    } catch (_) {}
    if (applied) selected = applied;
    // Upgrade existing browsers immediately, without requiring another Save click.
    persist();
    function persist() {
      // Vehicles are stored oldest first; keep only the most recent selections.
      garage = garage.slice(-maxVehicles);
      unresolved = unresolved.slice(
        unresolved.length - (maxVehicles - garage.length),
      );
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            version: 2,
            active: selected?.id || unresolvedSelection?.key || null,
            selection: selected ? compact(selected) : unresolvedSelection,
            vehicles: [...unresolved, ...garage.map(compact)],
          }),
        );
        if (selected) {
          localStorage.setItem(selectionKey, JSON.stringify(compact(selected)));
          try {
            sessionStorage.setItem(
              selectionKey,
              JSON.stringify(compact(selected)),
            );
          } catch (_) {}
        } else {
          localStorage.removeItem(selectionKey);
          try {
            sessionStorage.removeItem(selectionKey);
          } catch (_) {}
        }
      } catch (_) {
        status.textContent =
          "Your browser could not save your garage. This selection can still be used.";
      }
    }
    function selectionUrl(v) {
      const target = new URL(location.href);
      for (const key of [...target.searchParams.keys()]) {
        if (
          /^filter\.p\.m\.(fitment|derived_fitment|evp_fitment_poc)\./.test(key)
        )
          target.searchParams.delete(key);
      }
      const parts = target.pathname.split("/");
      const index = parts.indexOf("collections");
      if (index >= 0) {
        const tags = (parts[index + 2] || "")
          .split("+")
          .filter(
            (tag) =>
              tag && !/^(type|make|model|year)_/i.test(decodeURIComponent(tag)),
          );
        if (v)
          tags.push(
            "make_" + handle(v.makeName),
            "model_" + handle(v.modelName),
            ...(v.year ? ["year_" + v.year] : []),
          );
        target.pathname =
          parts.slice(0, index + 2).join("/") +
          (tags.length ? "/" + tags.join("+") : "");
      }
      target.searchParams.delete("page");
      return target.href;
    }
    let countRequest;
    async function updateCompatibleCount() {
      countRequest?.abort();
      const countLabel = root.querySelector("[data-vehicle-count]");
      if (!countLabel) return;
      const control = root.querySelector("[data-vehicle-filter-control]");
      countLabel.textContent = "";
      control.classList.remove("is-empty");
      if (!selected) return;
      const controller = new AbortController();
      countRequest = controller;
      const target = new URL(selectionUrl(selected));
      target.searchParams.delete("sections");
      target.searchParams.set("section_id", "vehicle-compatible-count");
      try {
        const response = await fetch(target.href, {
          signal: controller.signal,
        });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(
          await response.text(),
          "text/html",
        );
        const value = html
          .querySelector("[data-compatible-product-count]")
          ?.getAttribute("data-compatible-product-count");
        if (
          !value ||
          !/^\d+$/.test(value) ||
          controller.signal.aborted ||
          !root.isConnected
        )
          return;
        const count = Number(value);
        countLabel.textContent = ` (${count.toLocaleString()})`;
        control.classList.toggle("is-empty", count === 0);
      } catch (_) {
        // Keep the checkbox usable if the optional count cannot be loaded.
      }
    }
    function choose(v) {
      unresolvedSelection = null;
      selected = v;
      garage = [...garage.filter((item) => item.id !== v.id), v];
      persist();
      location.assign(selectionUrl(v));
    }
    function render() {
      root.classList.toggle("has-vehicle", !!selected);
      root.querySelector("[data-garage-change]").hidden = !selected;
      open.textContent = selected ? label(selected) : "Choose my vehicle";
      filter.disabled = !selected;
      filter.checked = !!(applied && selected?.id === applied.id);
      updateCompatibleCount();
      list.replaceChildren();
      garage.forEach((v) => {
        const row = document.createElement("div");
        row.className = "vehicle-garage-row";
        const choice = document.createElement("label");
        choice.className = "vehicle-garage-choice";
        const use = document.createElement("input");
        use.type = "radio";
        use.name = radioGroup;
        use.value = v.id;
        use.checked = selected?.id === v.id;
        const name = document.createElement("span");
        name.textContent = label(v);
        choice.append(use, name);
        row.append(choice);
        use.addEventListener("change", () => {
          if (use.checked) choose(v);
        });
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "my-vehicle-link";
        remove.textContent = "Remove";
        remove.setAttribute("aria-label", "Remove " + label(v));
        remove.addEventListener("click", () => {
          garage = garage.filter((item) => item.id !== v.id);
          if (selected?.id === v.id || !garage.length) {
            selected = garage[0] || null;
            unresolvedSelection = null;
          }
          if (applied && (applied.id === v.id || !garage.length)) {
            applied = null;
            clearFiltersOnClose = true;
          }
          persist();
          render();
          (garage.length ? add : type).focus();
        });
        row.append(remove);
        list.append(row);
      });
      add.hidden = !garage.length;
      form.hidden = garage.length > 0;
    }
    function options(select, values, placeholder) {
      select.replaceChildren(new Option(placeholder, ""));
      values.forEach(([value, name]) => select.add(new Option(name, value)));
      select.disabled = !values.length;
    }
    function populateYears() {
      options(
        year,
        [
          ...new Set(
            models
              .filter((m) => m.type === type.value)
              .flatMap((m) => Object.keys(m.years)),
          ),
        ]
          .sort((a, b) => Number(b) - Number(a))
          .map((value) => [value, value]),
        "Select year",
      );
      populateMakes();
    }
    function populateMakes() {
      options(
        make,
        [
          ...new Map(
            models
              .filter((m) => m.type === type.value && m.years[year.value])
              .map((m) => [
                m.make,
                m.makeName === "Bodcat" ? "Bobcat" : m.makeName,
              ]),
          ).entries(),
        ].sort((a, b) => a[1].localeCompare(b[1])),
        "Select make",
      );
      populateModels();
    }
    function populateModels() {
      options(
        model,
        models
          .filter(
            (m) =>
              m.type === type.value &&
              m.years[year.value] &&
              m.make === make.value,
          )
          .sort((a, b) =>
            a.modelName.localeCompare(b.modelName, "en", { numeric: true }),
          )
          .map((m) => [m.model, m.modelName]),
        "Select model",
      );
      submit.disabled = true;
    }
    options(
      type,
      [...new Set(models.map((m) => m.type))].map((value) => [value, value]),
      "Select type",
    );
    type.value = applied?.type || selected?.type || "SXS";
    populateYears();
    type.addEventListener("change", populateYears);
    year.addEventListener("change", populateMakes);
    make.addEventListener("change", populateModels);
    model.addEventListener("change", () => {
      submit.disabled = !model.value;
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (
        !type.value ||
        !year.value ||
        !make.value ||
        !model.value ||
        !form.reportValidity()
      )
        return;
      const v = resolve({
        make: make.value,
        model: model.value,
        year: year.value,
      });
      if (v && v.type === type.value) choose(v);
    });
    function openGarage() {
      render();
      dialog.showModal();
    }
    open.addEventListener("click", openGarage);
    root
      .querySelector("[data-garage-change]")
      .addEventListener("click", openGarage);
    root
      .querySelector("[data-garage-close]")
      .addEventListener("click", () => dialog.close());
    dialog.addEventListener("close", () => {
      if (clearFiltersOnClose) location.assign(selectionUrl(null));
    });
    dialog.addEventListener("click", (event) => {
      if (
        event.target === dialog &&
        (event.clientX < dialog.getBoundingClientRect().left ||
          event.clientX > dialog.getBoundingClientRect().right ||
          event.clientY < dialog.getBoundingClientRect().top ||
          event.clientY > dialog.getBoundingClientRect().bottom)
      )
        dialog.close();
    });
    add.addEventListener("click", () => {
      form.hidden = false;
      type.focus();
    });
    filter.addEventListener("change", () => {
      if (filter.checked && selected) choose(selected);
      else location.assign(selectionUrl(null));
    });
    render();
  }
  const initializeAll = () =>
    document.querySelectorAll("[data-my-vehicle]").forEach(initialize);
  initializeAll();
  document.addEventListener("DOMContentLoaded", initializeAll);
  document.addEventListener("shopify:section:load", initializeAll);
  document.addEventListener("evp:collection-updated", initializeAll);
})();
